/**
 * Rounds a floating-point number **down** to a specified number of fractional digits.
 * This function truncates the number without rounding, preserves precision,
 * avoids exponential notation, and removes trailing zeros.
 *
 * @param {number} value - The number to truncate.
 * @param {number} fractionDigits - The number of digits to keep after the decimal point. Must be >= 0.
 * @returns {string} The truncated number as a string, without unnecessary trailing zeros.
 *
 * @throws {RangeError} If fractionDigits is negative.
 *
 * @example
 * roundDown(1.2789, 2); // "1.27"
 * roundDown(1234.56789012, 8); // "1234.56789012"
 * roundDown(0.000000000001, 12); // "0.000000000001"
 * roundDown(5.9999, 3); // "5.999"
 */
export function roundDown(value: number, fractionDigits: number): string {
  if (fractionDigits < 0) {
    throw new RangeError('fractionDigits must be greater than 0');
  }

  const absFractionDigits = Math.trunc(fractionDigits);

  if (!Number.isFinite(value)) return String(value);

  // number to string without exp
  let raw = String(value);

  if (raw.includes('e') || raw.includes('E')) {
    raw = value.toLocaleString('en-US', {
      useGrouping: false,
      maximumFractionDigits: 50,
    });
  }

  const [int, frac = ''] = raw.split('.');

  if (absFractionDigits === 0) return int;

  // cut fraction
  const cut = frac.slice(0, absFractionDigits);

  let out = cut ? `${int}.${cut}` : int;

  // remove zeros at the end
  if (out.includes('.')) {
    out = out.replace(/\.?0+$/, '');
  }

  return out;
}
